/**
 * Stages (fase 2, steg 4 — second delivery).
 *
 * ---------------------------------------------------------------------------------------------
 * THE FESTIVAL'S STAGES ARE A TEMPLATE; A YEAR GETS ITS OWN COPY (decision 0020, sak 50).
 *
 * `stages` carries a required `festival_id` and a NULLABLE `edition_id`, and that one nullable
 * column is the whole feature:
 *
 *   edition_id IS NULL  →  the festival's TEMPLATE. The rows a new year is stamped from.
 *   edition_id = <year> →  that year's own stages. The only ones anybody plays on.
 *
 * `ux_stages__scope_key` is `(organization_id, festival_id, edition_id, key)` with
 * `nullsNotDistinct`, so ONE key can exist at both levels at once — which is what lets a year hold
 * a copy under the template's own key, and what lets the copy run `on conflict do nothing` and mean
 * it. The schema was built for two levels before the question was asked, and it did not have to
 * change when the answer did.
 *
 * WHEN THE COPY HAPPENS: creating a year (`EditionsService.create`) stamps the template in;
 * `POST /v1/editions/:id/clone` tops it up from last year, which is where a stage that year put up
 * on top of the template comes from. After that the year owns its rows — renaming one changes one
 * year, deleting one is a real deletion, and neither reaches the template or another year.
 *
 * THIS REPLACES A RESOLUTION (decision 0015). A year used to INHERIT the festival's set and be able
 * to OVERRIDE a row of it, resolved into an effective list at read time. Everything about that
 * worked except the thing Atle needed: "en må kunne slette og endre scener mellom årganger." There
 * was no tombstone, so a stage the festival always had turned up in a year that did not use it —
 * the limitation this header used to carry, now answered by changing the model rather than
 * inventing a suppression mechanism.
 *
 * `source` ON THE DTO SURVIVES AND STILL MEANS THE ROW'S LEVEL: `festival` is a template row,
 * `edition` is a year's own. Within one year's list every row is now `edition`; the field earns its
 * keep on the festival's own page, and as the thing that says a template row is not a stage.
 *
 * ---------------------------------------------------------------------------------------------
 * A PERFORMANCE MAY ONLY USE A STAGE OF ITS OWN EDITION (decision 0015 answer B, narrowed by 0020).
 *
 * Three refusals, and only the first is obvious. A stage of another FESTIVAL is the one everybody
 * pictures. A stage of a DIFFERENT YEAR of the same festival is the one nobody does — and a clone
 * makes it likely. The third is a TEMPLATE row: right festival, alive, and a perfectly legal
 * foreign key, but not a stage anybody plays on.
 *
 * The rule lives in the performances service rather than in a database constraint because it spans
 * three tables and a nullable column; the constraint that would express it is a trigger, and a
 * trigger that runs on every insert to enforce something the API already refuses is cost without
 * cover. Both halves are asserted against a real database.
 * ---------------------------------------------------------------------------------------------
 */
import { z } from 'zod';

import { cursorQuerySchema, page } from '../http/pagination.js';

export const STAGE_SORT_KEYS = ['order'] as const;
export type StageSortKey = (typeof STAGE_SORT_KEYS)[number];

/**
 * Where a stage in an effective list came from.
 *
 * Not stored — `edition_id` already holds it — but computed and returned, because the client has a
 * decision that turns on it. Editing a `festival` row from inside a year changes every year;
 * editing an `edition` row changes one. A UI that could not tell them apart would have to ask.
 */
export const STAGE_SOURCES = ['festival', 'edition'] as const;
export const stageSourceSchema = z.enum(STAGE_SOURCES).meta({ id: 'StageSource' });
export type StageSource = (typeof STAGE_SOURCES)[number];

export const stageDtoSchema = z
  .object({
    id: z.uuid(),
    festivalId: z.uuid(),
    /** Null for the festival's standard set; the year's id for a stage that belongs to one year. */
    editionId: z.uuid().nullable(),
    /** Generated once from the first name and never moved: the override matches on it. */
    key: z.string(),
    name: z.string(),
    capacity: z.number().int().positive().nullable(),
    sortOrder: z.number().int(),
    /**
     * `edition` when this row overrides or adds to the festival's set, `festival` when it is
     * inherited. Derived from `editionId` against the edition being asked about, so the same row
     * is `edition` in its own year and never appears in another.
     */
    source: stageSourceSchema,
    /**
     * Ressursen scenen ER (A19, 271.1): stedet på kartet og navnet bor på den. Null for en scene
     * uten ressurs (malens scener kan mangle den).
     */
    resourceId: z.uuid().nullable().optional(),
  })
  .meta({ id: 'Stage' });

/**
 * The list query, and the ONE field that decides what a caller gets back.
 *
 * `festivalId` alone returns the standard set — the list festival settings edits, where every row
 * is `source: 'festival'`. Adding `editionId` returns the EFFECTIVE list for that year: the
 * standard set with the year's overrides applied. They are deliberately one endpoint rather than
 * two, because they answer the same question at two scopes and a second route would duplicate the
 * resolution rule in a second place.
 *
 * Neither is optional. A stage without a festival is not a thing the schema can hold, and a list
 * that spanned every festival in the organisation is a page nobody has asked for.
 */
export const listStagesQuerySchema = cursorQuerySchema(STAGE_SORT_KEYS)
  .extend({
    festivalId: z.uuid(),
    /** Absent = the festival's standard set. Present = that year's effective list. */
    editionId: z.uuid().optional(),
  })
  .meta({ id: 'ListStagesQuery' });

export const stageListResponseSchema = page(stageDtoSchema, 'StageListResponse');

/**
 * Creating one.
 *
 * `editionId` is what decides which level it lands at, so it is a field of the request and not of
 * the path: the same form creates a festival stage and a one-year stage, and which it made is a
 * thing the person chose rather than a thing the route implied.
 *
 * No `key`. It is generated from the name exactly once, the way a festival's slug is, because a key
 * that can be edited is a key an override can lose its grip on.
 *
 * ---------------------------------------------------------------------------------------------
 * `overridesStageId` IS WHAT LETS A YEAR RENAME A STAGE, AND WITHOUT IT THE FEATURE IS HALF BUILT.
 *
 * An override is matched on `key`, and `key` is minted from the name — so the first cut of this
 * contract could only override a festival stage by keeping its name exactly. That rules out the
 * single most common reason a year overrides one at all: "Hovedscenen" is "Sparebank1-scenen" in
 * 2027 and back again in 2028. This header even cited that case as a motivation while the schema
 * refused it; a differently-named create minted a different key and quietly became a FIFTH row in
 * the picker beside the stage it was meant to replace.
 *
 * So an override says which stage it replaces, and inherits that stage's key. The name is then
 * free. It is the id and not the key on the wire because the client is holding the row it is
 * overriding — it just drew it — and an id is a thing the API can refuse precisely.
 *
 * WHAT THE API MUST CHECK, and both halves matter: the referenced stage has to be FESTIVAL-level
 * (`edition_id is null`) and of the same festival. Overriding an override is not a level this
 * model has, and overriding another festival's stage would import a key that means nothing here.
 * ---------------------------------------------------------------------------------------------
 */
export const createStageRequestSchema = z
  .object({
    festivalId: z.uuid(),
    /** Omitted or null for the festival's standard set. */
    editionId: z.uuid().nullish(),
    /**
     * The festival stage this one replaces for the year. Its key is inherited, so `name` is free.
     * Only meaningful with an `editionId` — the standard set has nothing to override.
     */
    overridesStageId: z.uuid().nullish(),
    name: z.string().trim().min(1).max(120),
    capacity: z.number().int().positive().max(1_000_000).nullish(),
    sortOrder: z.number().int().min(0).max(10_000).optional(),
  })
  .refine((body) => body.overridesStageId == null || body.editionId != null, {
    message: 'An override belongs to an edition.',
    path: ['overridesStageId'],
  })
  .meta({ id: 'CreateStageRequest' });

/**
 * Everything except which festival or which year it belongs to.
 *
 * A stage that changed level would silently rewrite history: performances already booked on it
 * would follow it out of the year they were booked in. Moving a stage between levels is creating
 * one and deleting the other, which is also what it is — the same reason
 * `updatePerformanceRequestSchema` has no `editionId`.
 */
export const updateStageRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(120).optional(),
    capacity: z.number().int().positive().max(1_000_000).nullish(),
    sortOrder: z.number().int().min(0).max(10_000).optional(),
  })
  .refine((body) => Object.keys(body).length > 0, {
    message: 'At least one field must be present.',
  })
  .meta({ id: 'UpdateStageRequest' });

export type StageDto = z.infer<typeof stageDtoSchema>;
export type ListStagesQuery = z.infer<typeof listStagesQuerySchema>;
export type StageListResponse = z.infer<typeof stageListResponseSchema>;
export type CreateStageRequest = z.infer<typeof createStageRequestSchema>;
export type UpdateStageRequest = z.infer<typeof updateStageRequestSchema>;

/**
 * Hva slettingen vil møte, spurt før den trykkes (sak 217.15). Et tall og ikke en liste: dialogen
 * sier «N programposter står på denne scenen», og hvilke det er, står i Lineup.
 */
export const stageUsageDtoSchema = z
  .object({
    performanceCount: z.number().int().min(0),
  })
  .meta({ id: 'StageUsage' });

export type StageUsageDto = z.infer<typeof stageUsageDtoSchema>;

/**
 * «SCENER» SOM EN VISNING AV RESSURSENE (QA13 256.8, grep C2, 0946).
 *
 * Ressursen er stedets ene navn, og scenen er merket «brukes i programmet» på den. Én rad per
 * ressurs i festivalen, med scenen i omfanget når en peker på den (`stageId`). En rad uten scene er
 * en ressurs som kan tas inn i programmet. Navnene kan være ulike: et år som døper om sin scene,
 * beholder navnet i det året, og ressursen sitt (sak 50).
 */
export const programmeResourceDtoSchema = z
  .object({
    resourceId: z.uuid(),
    resourceName: z.string(),
    typeName: z.string(),
    ownerName: z.string().nullable(),
    archived: z.boolean(),
    stageId: z.uuid().nullable(),
    stageName: z.string().nullable(),
  })
  .meta({ id: 'ProgrammeResource' });

export const programmeResourceListResponseSchema = z
  .object({ items: z.array(programmeResourceDtoSchema) })
  .meta({ id: 'ProgrammeResourceListResponse' });

/** Samme omfang som lista: malen uten `editionId`, året med. */
export const listProgrammeResourcesQuerySchema = z
  .object({
    festivalId: z.uuid(),
    editionId: z.uuid().optional(),
  })
  .meta({ id: 'ListProgrammeResourcesQuery' });

/**
 * «Legg til fra ressursene»: merker en ressurs «brukes i programmet» i omfanget. Navnet er
 * ressursens — det er hele poenget — så det sendes ikke.
 */
export const createStageFromResourceRequestSchema = z
  .object({
    festivalId: z.uuid(),
    editionId: z.uuid().nullish(),
    resourceId: z.uuid(),
    sortOrder: z.number().int().min(0).max(10_000).optional(),
  })
  .meta({ id: 'CreateStageFromResourceRequest' });

export type ProgrammeResourceDto = z.infer<typeof programmeResourceDtoSchema>;
export type ProgrammeResourceListResponse = z.infer<typeof programmeResourceListResponseSchema>;
export type ListProgrammeResourcesQuery = z.infer<typeof listProgrammeResourcesQuerySchema>;
export type CreateStageFromResourceRequest = z.infer<typeof createStageFromResourceRequestSchema>;
